import { apiClient } from '@borradh-workspace/api-client';
import type { QueryConfig } from '@borradh-workspace/api-client/react-query';
import type { LeadSourceTab } from '@borradh-workspace/labels';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-keys';

/**
 * Response of `GET /leads/stage-counts` (unify-customers PR 1).
 *
 * `stages` counts every lead by its pipeline stage; `tabs` is the same data
 * pre-grouped for the Customers tab strip (`tabs.leads` is every unconverted
 * stage, `tabs.clients = stages.booked`). There is no contracts/api-client type
 * for this endpoint yet, so the shape is declared here.
 */
export interface LeadStageCounts {
  stages: {
    new: number;
    contacted: number;
    qualified: number;
    booked: number;
    lost: number;
  };
  tabs: { all: number; leads: number; clients: number; lost: number };
  /**
   * Leads tab only: unconverted leads per source bucket. Optional so a newer
   * bundle against an older API still renders (the strip shows "All leads").
   */
  leadSources?: Record<LeadSourceTab, number>;
}

export const leadStageCountsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.leads.stageCounts(),
    queryFn: () => apiClient.get<LeadStageCounts>('leads/stage-counts'),
    staleTime: 60 * 1000,
  });

/**
 * Per-tab / per-stage lead counts for the Clients tab badges. Invalidated by
 * `useSetLeadStage` and any lead mutation via the shared `['leads']` key.
 */
export const useLeadStageCounts = (
  queryConfig?: QueryConfig<typeof leadStageCountsQueryOptions>
) => {
  const query = useQuery({ ...leadStageCountsQueryOptions(), ...queryConfig });

  return {
    counts: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
